import { Link, createFileRoute } from "@tanstack/react-router";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks, HowWeTested } from "@/components/eeat-blocks";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/verantwoord-spelen";
import { getAuthor } from "@/lib/authors";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { ogImageMeta } from "@/lib/og";
import { SITE } from "@/lib/site";

const PATH = "/verantwoord-spelen";
const URL = `${SITE.url}${PATH}/`;

export const Route = createFileRoute("/verantwoord-spelen")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: "Verantwoord Spelen: Stortlimieten, Pauzes en Hulp" },
        { name: "description", content: guide.description },
        { property: "og:title", content: "Verantwoord Spelen: Stortlimieten, Pauzes en Hulp" },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("verantwoord-spelen"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Verantwoord spelen", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: ResponsibleGamingPage,
});

function ResponsibleGamingPage() {
  const budget = new LinkBudget(PATH);

  const takeaways = [
    "Een limiet bij je bank of wallet werkt breder dan een limiet bij één aanbieder.",
    "Verlagen van een limiet gaat direct in, verhogen pas na een wachttijd.",
    "Meerdere stortingen op één avond is het duidelijkste signaal dat je budget schuift.",
    "Speel nooit met geleend geld: geen creditcard, roodstand of achteraf betalen.",
    "Cruks sluit je uit bij alle vergunde aanbieders; een afkoelperiode is de lichtere stap.",
    "Loket Kansspel is gratis en 24 uur per dag bereikbaar op 0800 022 5555.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <nav aria-label="Broodkruimels" className="mb-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Verantwoord spelen</span>
      </nav>

      <h1 className="max-w-4xl text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
        Verantwoord spelen: grenzen stellen via je betalingen
      </h1>
      <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{guide.description}</p>

      <div className="mt-6 max-w-3xl">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>

      <div className="mt-8 max-w-3xl">
        <KeyTakeaways items={takeaways} />
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <HowWeTested
            subject="de limietinstellingen van banken, wallets en aanbieders"
            providers={10}
            period="november 2026"
            extra="We hebben bij elke aanbieder uit onze lijst zelf een stortings- en verlieslimiet ingesteld en gecontroleerd hoe snel een verlaging en een verhoging actief werden."
          />

          <section className="mt-12" aria-label="Verantwoord spelen in de praktijk">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="verantwoord spelen"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over verantwoord spelen
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
            />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
